import {Assert, IsNaN} from "js-vextensions";
import {AddSchema} from "mobx-graphlink";
import * as THREE from "three";

function IsNullOrNaN(value: number|null|undefined) {
	return value === null || IsNaN(value);
}

const hasOwnProperty = Object.prototype.hasOwnProperty;
const hasOwnProp = (obj: any, prop: string)=>hasOwnProperty.call(obj, prop);

export type Vec2Shape = {x: number, y: number};
export function IsVec2iShape(obj: any): obj is {x: number, y: number} {
	return hasOwnProp(obj, "x") && hasOwnProp(obj, "y");
}

export type Vec2Simple = [number, number];
export class Vec2 {
	static get zero() { return new Vec2(0, 0); }
	static get one() { return new Vec2(1, 1); }
	static FromSimple(vec: Vec2Simple) { return new Vec2(vec[0], vec[1]); }
	ToSimple() { return [this.x, this.y] as Vec2Simple; }

	constructor(x?: number, y?: number);
	constructor(pos: {x: number, y: number});
	constructor(pos: {left: number, top: number});
	constructor(...args) {
		var x = 0, y = 0;
		if (typeof args[0] == "number") [x, y] = args;
		else if (args[0] && args[0].x != null) [x, y] = [args[0].x, args[0].y];
		else if (args[0] && args[0].left != null) [x, y] = [args[0].left, args[0].top];

		Assert(!IsNullOrNaN(x) && !IsNullOrNaN(y), "Cannot initialize Vec2's x/y to null/NaN. (if needed, initialize to undefined)");

		this.x = x;
		this.y = y;
	}

	x: number;
	y: number;

	toString() { return `${this.x} ${this.y}`; }
	Equals(other) { return other && this.toString() == other.toString(); }

	NewX(xOrFunc: number | ((oldX: number)=>number)) { return new Vec2(xOrFunc instanceof Function ? xOrFunc(this.x) : xOrFunc, this.y); }
	NewY(yOrFunc: number | ((oldY: number)=>number)) { return new Vec2(this.x, yOrFunc instanceof Function ? yOrFunc(this.y) : yOrFunc); }

	Plus(other: Vec2Shape): Vec2;
	Plus(otherX: number, otherY: number): Vec2;
	Plus(...args) {
		const [x, y] = IsVec2iShape(args[0]) ? [args[0].x, args[0].y] : args;
		return new Vec2(this.x + x, this.y + y);
	}
	Minus(other: Vec2Shape): Vec2;
	Minus(otherX: number, otherY: number): Vec2;
	Minus(...args) {
		const [x, y] = IsVec2iShape(args[0]) ? [args[0].x, args[0].y] : args;
		return new Vec2(this.x - x, this.y - y);
	}

	Times(other: Vec2Shape): Vec2;
	Times(other: number): Vec2;
	Times(otherX: number, otherY: number): Vec2;
	Times(...args) {
		var [x, y] = IsVec2iShape(args[0]) ? [args[0].x, args[0].y] :
			args.length == 1 ? [args[0], args[0]] :
			args;
		return new Vec2(this.x * x, this.y * y);
	}
	DividedBy(other: Vec2): Vec2;
	DividedBy(other: number): Vec2;
	DividedBy(otherX: number, otherY: number): Vec2;
	DividedBy(...args) {
		var [x, y] = IsVec2iShape(args[0]) ? [args[0].x, args[0].y] :
			args.length == 1 ? [args[0], args[0]] :
			args;
		return new Vec2(this.x / x, this.y / y);
	}

	DistanceTo(other: Vec2) {
		return Math.sqrt((other.x - this.x).ToPower(2) + (other.y - this.y).ToPower(2));
	}

	AsArray() {
		return [this.x, this.y];
	}
	ToVec3(z = 0) {
		return new Vec3(this.x, this.y, z);
	}

	// shortcuts through Vec3
	To3JPos(z = 0) {
		return this.ToVec3(z).To3JPos();
	}
	static From3JPos(pos: THREE.Vector3) {
		return Vec3.From3JPos(pos).ToVec2();
	}
}
AddSchema("Vec2", {
	properties: {
		x: {type: "number"},
		y: {type: "number"},
	},
	required: ["x", "y"],
});

export type Vec3Shape = {x: number, y: number, z: number};
export function IsVec3Shape(obj: any): obj is {x: number, y: number, z: number} {
	return hasOwnProp(obj, "x") && hasOwnProp(obj, "y") && hasOwnProp(obj, "z");
}

export type Vec3Simple = [number, number, number];
export class Vec3 {
	static get zero() { return new Vec3(0, 0, 0); }
	static get one() { return new Vec3(1, 1, 1); }
	static FromSimple(vec: Vec3Simple) { return new Vec3(vec[0], vec[1], vec[2]); }

	constructor(x?: number, y?: number, z?: number);
	constructor(pos: {x: number, y: number, z: number});
	constructor(...args) {
		var x = 0, y = 0, z = 0;
		if (typeof args[0] == "number") [x, y, z] = args;
		else if (args[0] && args[0].x != null) [x, y, z] = [args[0].x, args[0].y, args[0].z];

		Assert(!IsNullOrNaN(x) && !IsNullOrNaN(y) && !IsNullOrNaN(z), "Cannot initialize Vec3's x/y/z to null/NaN. (if needed, initialize to undefined)");

		this.x = x != null ? x : 0;
		this.y = y != null ? y : 0;
		this.z = z != null ? z : 0;
	}

	x: number;
	y: number;
	z: number;

	toString() { return `${this.x} ${this.y} ${this.z}`; }

	NewX(xOrFunc: number | ((oldX: number)=>number)) { return new Vec3(xOrFunc instanceof Function ? xOrFunc(this.x) : xOrFunc, this.y, this.z); }
	NewY(yOrFunc: number | ((oldY: number)=>number)) { return new Vec3(this.x, yOrFunc instanceof Function ? yOrFunc(this.y) : yOrFunc, this.z); }
	NewZ(zOrFunc: number | ((oldZ: number)=>number)) { return new Vec3(this.x, this.y, zOrFunc instanceof Function ? zOrFunc(this.z) : zOrFunc); }

	Minus(other: Vec3Shape): Vec3;
	Minus(otherX: number, otherY: number, otherZ: number): Vec3;
	Minus(...args) {
		const [x, y, z] = IsVec3Shape(args[0]) ? [args[0].x, args[0].y, args[0].z] : args;
		return new Vec3(this.x - x, this.y - y, this.z - z);
	}
	Plus(other: Vec3Shape): Vec3;
	Plus(otherX: number, otherY: number, otherZ: number): Vec3;
	Plus(...args) {
		const [x, y, z] = IsVec3Shape(args[0]) ? [args[0].x, args[0].y, args[0].z] : args;
		return new Vec3(this.x + x, this.y + y, this.z + z);
	}
	Times(other: Vec3Shape): Vec3;
	Times(other: number): Vec3;
	Times(otherX: number, otherY: number, otherZ: number): Vec3;
	Times(...args) {
		var [x, y, z] = IsVec3Shape(args[0]) ? [args[0].x, args[0].y, args[0].z] :
			args.length == 1 ? [args[0], args[0], args[0]] :
			args;
		return new Vec3(this.x * x, this.y * y, this.z * z);
	}

	DistanceTo(other: Vec3) {
		return Math.sqrt((other.x - this.x).ToPower(2) + (other.y - this.y).ToPower(2) + (other.z - this.z).ToPower(2));
	}

	AsArray() {
		return [this.x, this.y, this.z];
	}
	ToVec2() {
		return new Vec2(this.x, this.y);
	}
	To3JPos() {
		return new THREE.Vector3(this.x, this.y, this.z);
	}
	To3JRot() {
		return new THREE.Euler(this.x, this.y, this.z);
	}
	static From3JPos(pos: THREE.Vector3) {
		return new Vec3(pos.x, pos.y, pos.z);
	}
}
AddSchema("Vec3", {
	properties: {
		x: {type: "number"},
		y: {type: "number"},
		z: {type: "number"},
	},
	required: ["x", "y", "z"],
});

export type RectShape = {x: number, y: number, width: number, height: number};
export function IsRectShape(obj: any): obj is {x: number, y: number, z: number} {
	return hasOwnProp(obj, "x") && hasOwnProp(obj, "y") && hasOwnProp(obj, "width") && hasOwnProp(obj, "height");
}

export class Rect {
	static FromCenter(center: Vec2, size: Vec2, yPlusMeansUp = true) {
		return new Rect(center.Minus(size.Times(0.5)), size, yPlusMeansUp);
	}
	static FromLTWH(rect: {left: number, top: number, width: number, height: number}, yPlusMeansUp = true) { // in this repo, we use blender-style coordinates, where y+ means upwards/forward
		// Why don't we use [rect.top + rect.height] for the y value? Well... the problem is we don't know what the size is of the overall container, so we can't recalc the rect's bottom in terms of "dist from container bottom".
		return new Rect(rect.left, rect.top, rect.width, rect.height, yPlusMeansUp);
	}

	constructor(pos: Vec2, size: Vec2, yPlusMeansUp: boolean);
	constructor(x: number, y: number, width: number, height: number, yPlusMeansUp: boolean);
	constructor(...args) {
		let x: number, y: number, width: number, height: number, yPlusMeansUp: boolean;
		if (args.length == 2 || args.length == 3) [x, y, width, height, yPlusMeansUp] = [args[0].x, args[0].y, args[1].x, args[1].y, args[2]];
		else [x, y, width, height, yPlusMeansUp] = args;

		Assert(!IsNullOrNaN(x) && !IsNullOrNaN(y) && !IsNullOrNaN(width) && !IsNullOrNaN(height),
			"Cannot initialize Rect's x/y/width/height to null/NaN. (if needed, initialize to undefined)");

		this.x = x;
		this.y = y;
		this.width = width != null ? width : 0;
		this.height = height != null ? height : 0;
		//this.yPlusMeansUp = yPlusMeansUp != null ? yPlusMeansUp : false;
		if (yPlusMeansUp != null) this.yPlusMeansUp = yPlusMeansUp;
	}

	x: number;
	y: number;
	width: number;
	height: number;
	yPlusMeansUp: boolean;
	// I'm not sure if any pathway in this app can result in a Rect instance with yPlusMeansUp being null (I think there's none), but keeping this check for now to be sure.
	AssertYPlusDirKnown() {
		Assert(this.yPlusMeansUp != null, "To use Bottom/Top accessors, yPlusMeansUp must be defined as either true or false.");
	}

	get Left() { return this.x; }
	set Left(val: number) {
		var oldRight = this.Right;
		this.x = val;
		this.Right = oldRight;
	}
	get Right() { return this.x + this.width; }
	set Right(val: number) { this.width = val - this.x; }
	get Bottom() {
		this.AssertYPlusDirKnown();
		return this.yPlusMeansUp ? this.y : this.y + this.height;
	}
	set Bottom(val: number) {
		this.AssertYPlusDirKnown();
		if (this.yPlusMeansUp) {
			var oldTop = this.Top;
			this.y = val;
			this.Top = oldTop;
		} else {
			this.height = val - this.y;
		}
	}
	get Top() {
		this.AssertYPlusDirKnown();
		return this.yPlusMeansUp ? this.y + this.height : this.y;
	}
	set Top(val: number) {
		this.AssertYPlusDirKnown();
		if (this.yPlusMeansUp) {
			this.height = val - this.y;
		} else {
			const oldBottom = this.Bottom;
			this.y = val;
			this.Bottom = oldBottom;
		}
	}
	get Position() { return new Vec2(this.x, this.y); }
	set Position(val: Vec2) {
		this.x = val.x;
		this.y = val.y;
	}
	get Size() { return new Vec2(this.width, this.height); }
	set Size(val: Vec2) {
		this.width = val.x;
		this.height = val.y;
	}

	get Center() { return new Vec2(this.x + (this.width / 2), this.y + (this.height / 2)); }
	set Center(val: Vec2) {
		const offset = val.Minus(this.Center);
		this.Position = this.Position.Plus(offset);
	}

	/*@_VDFDeserialize() Deserialize(node) {
		var strParts = node.primitiveValue.split(" ");
		this.x = parseInt(strParts[0]);
		this.y = parseInt(strParts[1]);
		this.width = parseInt(strParts[2]);
		this.height = parseInt(strParts[3]);
	}
	@_VDFSerialize() Serialize() { return new VDFNode(this.toString()); }*/
	toString() { return `${this.x} ${this.y} ${this.width} ${this.height}`; }

	Equals(other) {
		if (!(other instanceof Rect)) return false;
		return this.toString() == other.toString();
	}

	NewX(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({x: valOrFunc instanceof Function ? valOrFunc(this.x) : valOrFunc});
	}
	NewLeft(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({Left: valOrFunc instanceof Function ? valOrFunc(this.Left) : valOrFunc});
	}
	NewRight(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({Right: valOrFunc instanceof Function ? valOrFunc(this.Right) : valOrFunc});
	}
	NewY(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({y: valOrFunc instanceof Function ? valOrFunc(this.y) : valOrFunc});
	}
	NewBottom(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({Bottom: valOrFunc instanceof Function ? valOrFunc(this.Bottom) : valOrFunc});
	}
	NewTop(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({Top: valOrFunc instanceof Function ? valOrFunc(this.Top) : valOrFunc});
	}
	NewPosition(valOrFunc: Vec2 | ((oldVal: Vec2)=>Vec2)) {
		return (this.Clone()).VSet({Position: valOrFunc instanceof Function ? valOrFunc(this.Position) : valOrFunc});
	}
	NewWidth(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({width: valOrFunc instanceof Function ? valOrFunc(this.width) : valOrFunc});
	}
	NewHeight(valOrFunc: number | ((oldVal: number)=>number)) {
		return (this.Clone()).VSet({height: valOrFunc instanceof Function ? valOrFunc(this.height) : valOrFunc});
	}
	NewSize(valOrFunc: Vec2 | ((oldVal: Vec2)=>Vec2)) {
		return (this.Clone()).VSet({Size: valOrFunc instanceof Function ? valOrFunc(this.Size) : valOrFunc});
	}

	Grow(amountOnEachSide: number) {
		return new Rect(this.x - amountOnEachSide, this.y - amountOnEachSide, this.width + (amountOnEachSide * 2), this.height + (amountOnEachSide * 2), this.yPlusMeansUp);
	}
	Encapsulating(rect: RectShape) {
		var posX = Math.min(this.x, rect.x);
		var posY = Math.min(this.y, rect.y);
		return new Rect(posX, posY, Math.max(this.x + this.width, rect.x + rect.width) - posX, Math.max(this.y + this.height, rect.y + rect.height) - posY, this.yPlusMeansUp);
	}
	Encapsulate(rect: RectShape) {
		var oldRight = this.x + this.width;
		var oldBottom = this.y + this.height;
		this.x = Math.min(this.x, rect.x);
		this.y = Math.min(this.y, rect.y);
		this.width = Math.max(oldRight, rect.x + rect.width) - this.x;
		this.height = Math.max(oldBottom, rect.y + rect.height) - this.y;
	}

	Intersects(other: Rect, countTouchAsIntersect = false) {
		/*this.AssertYPlusDirKnown();
		other.AssertYPlusDirKnown();*/

		// Convert y coordinates to consistent min/max bounds (since the two rects *could* have different y+ directions)
		const thisMinY = Math.min(this.y, this.y + this.height);
		const thisMaxY = Math.max(this.y, this.y + this.height);
		const otherMinY = Math.min(other.y, other.y + other.height);
		const otherMaxY = Math.max(other.y, other.y + other.height);

		if (countTouchAsIntersect) {
			return this.Left <= other.Right && this.Right >= other.Left && thisMinY <= otherMaxY && thisMaxY >= otherMinY;
		}
		return this.Left < other.Right && this.Right > other.Left && thisMinY < otherMaxY && thisMaxY > otherMinY;
	}
	/** Returns true if rect would intersect the other, when wrapped to the 2/8 potential "other-sides" of given frame/backdrop. (-x, +x, -y, +y, -x -y, -x +y, +x -y, +x +y)
	 * (note that it does the checks "stupidly", ie. just checking all possible switch-side variants, without checking if "switched side" version is actually on or even near the actual frame/backdrop) */
	Intersects_Advanced(other: Rect, options: {xWrappedBy?: number, yWrappedBy?: number}) {
		const variantsToCompare: Rect[] = [this];
		if (options.xWrappedBy) {
			variantsToCompare.push(...(variantsToCompare).SelectMany(base=>{
				return [base, base.NewX(x=>x - options.xWrappedBy!), base.NewX(x=>x + options.xWrappedBy!)];
			}));
		}
		if (options.yWrappedBy) {
			variantsToCompare.push(...(variantsToCompare).SelectMany(base=>{
				return [base, base.NewY(y=>y - options.yWrappedBy!), base.NewY(y=>y + options.yWrappedBy!)];
			}));
		}
		return (variantsToCompare).Any(a=>a.Intersects(other));
	}

	Clone() {
		return new Rect(this.x, this.y, this.width, this.height, this.yPlusMeansUp);
	}
}
AddSchema("Rect", {
	properties: {
		x: {type: "number"},
		y: {type: "number"},
		width: {type: "number"},
		height: {type: "number"},
		yPlusMeansUp: {type: "boolean"},
	},
	required: ["x", "y", "width", "height"],
});

export class VBounds {
	static FromCenter(center: Vec3, size: Vec3) {
		return new VBounds(center.Minus(size.Times(0.5)), size);
	}
	static FromMinMax(min: Vec3, max: Vec3) {
		return new VBounds(min, max.Minus(min));
	}
	constructor(position: Vec3, size: Vec3) {
		this.position = position;
		this.size = size;
	}
	position: Vec3;
	size: Vec3;

	get Center() {
		return this.position.Plus(this.size.Times(0.5));
	}

	Intersects(other: VBounds) {
		return this.position.x < other.position.x + other.size.x && this.position.x + this.size.x > other.position.x &&
			this.position.y < other.position.y + other.size.y && this.position.y + this.size.y > other.position.y &&
			this.position.z < other.position.z + other.size.z && this.position.z + this.size.z > other.position.z;
	}

	/*@_VDFDeserialize() Deserialize(node) {
		var parts = node.primitiveValue.split("|");
		var posParts = parts[0].split(" ");
		this.position = new VVec3(parseFloat(posParts[0]), parseFloat(posParts[1]), parseFloat(posParts[2]));
		var sizeParts = parts[1].split(" ");
		this.size = new VVec3(parseFloat(sizeParts[0]), parseFloat(sizeParts[1]), parseFloat(sizeParts[2]));
	}
	@_VDFSerialize() Serialize() { return new VDFNode(this.toString()); }*/
	toString() { return `${this.position.x} ${this.position.y} ${this.position.z}|${this.size.x} ${this.size.y} ${this.size.z}`; }
}
AddSchema("VBounds", {
	properties: {
		position: {$ref: "Vec3"},
		size: {$ref: "Vec3"},
	},
	required: ["position", "size"],
});